import React from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../../convex/_generated/api';
import { Button } from '../../../../catalyst-components/button';
import { Spinner } from '../../../../catalyst-components/spinner';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../../catalyst-components/table';
import { formatDuration } from '../../../../shared/utils/formatDuration';
import {
    ArrowLeftIcon,
    ClockIcon,
} from '@heroicons/react/24/outline';

export const ScreenTimeAnalytics: React.FC = () => {
    const navigate = useNavigate();
    const routeAnalytics = useQuery(api.analytics.getRouteAnalytics);
    const screenTimeSummaries = useQuery(api.analytics.getUserScreenTimeSummaries);

    // Calculate grand totals from user summaries
    const grandTotals = React.useMemo(() => {
        if (!screenTimeSummaries) return null;
        const values = Object.values(screenTimeSummaries);
        return {
            totalUsers: values.length,
            totalTime: values.reduce((sum, v) => sum + v.total, 0),
            activeTime: values.reduce((sum, v) => sum + v.active, 0),
            idleTime: values.reduce((sum, v) => sum + v.idle, 0),
        };
    }, [screenTimeSummaries]);

    if (!routeAnalytics) {
        return (
            <div className="min-h-screen bg-background pt-24">
                <div className="border-b border-border/50 bg-gradient-to-r from-green-50 to-green-100/50 px-6 py-8">
                    <div>
                        <div className="flex items-center gap-4 mb-4">
                            <Button color="secondary" className="rounded-full" onClick={() => navigate('/admin')}>
                                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                                Back
                            </Button>
                        </div>
                        <h1 className="text-3xl font-semibold text-foreground">Screen Time Analytics</h1>
                    </div>
                </div>
                <div className="flex justify-center items-center h-64">
                    <Spinner className="h-12 w-12" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background pt-24">
            {/* Header */}
            <div className="border-b border-border/50 bg-gradient-to-r from-green-50 to-green-100/50 px-6 py-8">
                <div>
                    <div className="flex items-center gap-4 mb-4">
                        <Button color="secondary" className="rounded-full" onClick={() => navigate('/admin')}>
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Back
                        </Button>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-white border-2 border-green-200">
                            <ClockIcon className="h-8 w-8 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-semibold text-foreground mb-1">Screen Time Analytics</h1>
                            <p className="text-base text-muted-foreground">
                                Time spent on each page across all users
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-6 py-8">
                <div className="space-y-8">
                    {/* Summary Stats */}
                    {grandTotals && (
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                                <span className="text-sm text-muted-foreground">Users Tracked: </span>
                                <span className="font-semibold text-foreground">{grandTotals.totalUsers}</span>
                            </div>
                            <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                                <span className="text-sm text-muted-foreground">Total Time: </span>
                                <span className="font-semibold text-foreground">{formatDuration(grandTotals.totalTime)}</span>
                            </div>
                            <div className="px-4 py-2 bg-green-50 border-2 border-green-200 rounded-full">
                                <span className="text-sm text-muted-foreground">Active: </span>
                                <span className="font-semibold text-green-700">{formatDuration(grandTotals.activeTime)}</span>
                            </div>
                            <div className="px-4 py-2 bg-orange-50 border-2 border-orange-200 rounded-full">
                                <span className="text-sm text-muted-foreground">Idle: </span>
                                <span className="font-semibold text-orange-600">{formatDuration(grandTotals.idleTime)}</span>
                            </div>
                        </div>
                    )}

                    {/* Route Breakdown Table */}
                    <div className="border-2 border-border/50 rounded-xl overflow-hidden bg-card">
                        <div className="px-6 py-4 border-b border-border/50 bg-muted/20">
                            <h2 className="text-lg font-semibold text-foreground">Time by Page</h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                Aggregate active and idle time across all users for each route
                            </p>
                        </div>
                        {routeAnalytics.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                No screen time data collected yet. Data will appear as users navigate the app.
                            </div>
                        ) : (
                            <Table>
                                <TableHead>
                                    <TableRow className="bg-muted/30">
                                        <TableHeader>Route</TableHeader>
                                        <TableHeader>Total Time</TableHeader>
                                        <TableHeader>Active Time</TableHeader>
                                        <TableHeader>Idle Time</TableHeader>
                                        <TableHeader>Views</TableHeader>
                                        <TableHeader>Unique Users</TableHeader>
                                        <TableHeader>Avg Duration</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {routeAnalytics.map((row) => (
                                        <TableRow key={row.route} className="hover:bg-muted/20">
                                            <TableCell className="font-mono text-sm font-medium text-foreground">
                                                {row.route}
                                            </TableCell>
                                            <TableCell className="font-mono text-sm">
                                                {formatDuration(row.total_ms)}
                                            </TableCell>
                                            <TableCell className="font-mono text-sm text-green-600">
                                                {formatDuration(row.active_ms)}
                                            </TableCell>
                                            <TableCell className="font-mono text-sm text-orange-500">
                                                {formatDuration(row.idle_ms)}
                                            </TableCell>
                                            <TableCell className="text-sm font-semibold">
                                                {row.views}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {row.unique_users}
                                            </TableCell>
                                            <TableCell className="font-mono text-sm text-muted-foreground">
                                                {formatDuration(row.avg_duration_ms)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
